import React, { useState } from "react"; // Added useState
import { Modal, Button, Row, Col } from "react-bootstrap";
import api from "../../api/axios";

const extractMeasurement = (unit) => {
  if (!unit?.measurement) return null;
  if (Array.isArray(unit.measurement)) {
    return unit.measurement.length ? unit.measurement[0] : null;
  }
  return unit.measurement;
};

const toText = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "string" && value.length) return value;
  if (value && typeof value === "object")
    return Object.values(value).join(", ");
  return "—";
};

export default function UnitQuickViewModal({ show, unit, onClose }) { 
  // State for the zoomed image
  const [selectedImage, setSelectedImage] = useState(null);

  if (!unit) return null;

  const measurement = extractMeasurement(unit);

  const getImageUrl = (photo) => {
    if (!photo) return "";
    return photo.file_path.startsWith("http")
      ? photo.file_path
      : `${api.defaults.baseURL}/${photo.file_path}`;
  };

  const primaryPhoto = unit.photos?.find((p) => p.is_primary) || unit.photos?.[0];

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Unit Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="p-3 border rounded shadow-sm">
          <Row>
            {/* LEFT SIDE SINGLE IMAGE */}
            <Col md={5}>
              {unit.photos && unit.photos.length > 0 ? (
                <>
                  <img
                    src={getImageUrl(primaryPhoto)}
                    alt="unit"
                    className="img-fluid rounded cursor-pointer"
                    style={{
                      width: "100%",
                      height: "350px",
                      objectFit: "cover",
                      border: "1px solid #ddd",
                      cursor: "zoom-in"
                    }}
                    onClick={() => setSelectedImage(getImageUrl(primaryPhoto))}
                  />
                  {unit.photos.length > 1 && (
                    <div className="row g-2 mt-2">
                      {unit.photos.slice(0, 4).map((photo, index) => (
                        <div key={photo.id || index} className="col-3">
                          <img
                            src={getImageUrl(photo)}
                            alt={`unit photo ${index + 1}`}
                            className="img-fluid rounded cursor-pointer"
                            style={{
                              height: "60px",
                              width: "100%",
                              objectFit: "cover",
                              border: "1px solid #eee",
                              cursor: "zoom-in"
                            }}
                            onClick={() => setSelectedImage(getImageUrl(photo))}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="bg-light d-flex align-items-center justify-content-center border rounded"
                  style={{ width: "100%", height: "350px" }}
                >
                  <span className="text-muted">No Photo Available</span>
                </div>
              )}
            </Col>

            {/* RIGHT SIDE DETAILS */}
            <Col md={7}>
              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Unit Number</Col>
                <Col xs={7}>{unit.unit_number || "—"}</Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Type</Col>
                <Col xs={7}>{unit.type?.name || "—"}</Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Purpose</Col>
                <Col xs={7}>{unit.purpose || "—"}</Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Marketing Officer (MO)</Col>
                <Col xs={7}>
                  {unit.marketing_officer_id
                    ? unit.marketing_officer?.name || "Unassigned"
                    : "Unassigned"}
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Marketing Manager (MM)</Col>
                <Col xs={7}>
                  {unit.marketing_manager_id
                    ? unit.marketing_manager?.name || "Unassigned"
                    : "Unassigned"}
                </Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Floor</Col>
                <Col xs={7}>{unit.floor ?? "—"}</Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Price</Col>
                <Col xs={7}>{unit.price ?? "—"}</Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Rent</Col>
                <Col xs={7}>{unit.rent_amount ?? "—"}</Col>
              </Row>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Facilities</Col>
                <Col xs={7}>{toText(unit.facilities)}</Col>
              </Row>

              <hr />

              <h6 className="fw-bold">Measurements</h6>

              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Area (sq-ft)</Col>
                <Col xs={7}>{measurement?.area_sq_ft ?? "—"}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Katha</Col>
                <Col xs={7}>{measurement?.katha ?? "—"}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Bigha</Col>
                <Col xs={7}>{measurement?.bigha ?? "—"}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Acre</Col>
                <Col xs={7}>{measurement?.acre ?? "—"}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="fw-semibold">Unit of Measure</Col>
                <Col xs={7}>{measurement?.unit_of_measure ?? "—"}</Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>

      {/* ZOOM LIGHTBOX OVERLAY */}
      {selectedImage && (
        <div 
          onClick={() => setSelectedImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out"
          }}
        >
          <img 
            src={selectedImage} 
            alt="Zoomed Unit" 
            style={{ maxHeight: "90%", maxWidth: "90%", borderRadius: "8px" }} 
          />
          <button 
            onClick={() => setSelectedImage(null)}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "none",
              border: "none",
              color: "white",
              fontSize: "35px",
              fontWeight: "bold"
            }}
          >
            ×
          </button>
        </div>
      )}
    </Modal>
  );
}